// valet-keychain-qr — pwa-qr-scanning: continuous-decode QR scanner.
//
// Distinct from CameraCapture (single still-frame photo capture): this opens
// the rear camera and continuously decodes frames until a QR code is found.
// Reuses CameraCapture's getUserMedia/permission/error scaffolding (facingMode
// environment, NotAllowed/NotFound/Overconstrained UI, stop-on-unmount) but
// adds a decode loop instead of a manual capture button.
//
// D2 — codes against the native `BarcodeDetector` Web API. The `barcode-detector`
// (Sec-ant/zxing-wasm) polyfill is LAZY-imported only when native is absent
// (iOS Safari) so the wasm chunk stays out of the initial bundle for the vast
// majority of devices that already support BarcodeDetector natively.
//
// `apps/pwa/src/shared/ui/` did not exist before this change (design open
// question (a)) — created here per the design's documented fallback.

import { useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { Flashlight, FlashlightOff, RotateCcw, ScanLine, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface QrScannerProps {
  /** Called once with the decoded text — the caller is responsible for closing the scanner. */
  onDecode: (text: string) => void;
  onCancel: () => void;
  /** Optional display label — mirrors CameraCapture's viewfinder text convention. */
  label?: string;
}

type ScannerState = 'idle' | 'permission' | 'scanning' | 'decoded' | 'error';
type CameraError =
  | 'NotAllowedError'
  | 'NotFoundError'
  | 'OverconstrainedError'
  | 'unsupported'
  | 'unknown';

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
}

function getNativeBarcodeDetectorCtor(): BarcodeDetectorCtor | null {
  const ctor = (globalThis as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  return ctor ?? null;
}

/**
 * Resolves a BarcodeDetector-shaped decoder — native when the browser
 * supports it, otherwise the lazy-loaded wasm ponyfill (never touches the
 * global scope, so it can't shadow a real native implementation elsewhere).
 */
async function loadDetector(): Promise<BarcodeDetectorLike> {
  const NativeCtor = getNativeBarcodeDetectorCtor();
  if (NativeCtor) {
    return new NativeCtor({ formats: ['qr_code'] });
  }
  const { BarcodeDetector: PolyfillDetector } = await import('barcode-detector/ponyfill');
  return new PolyfillDetector({ formats: ['qr_code'] }) as unknown as BarcodeDetectorLike;
}

/** requestAnimationFrame when available (real browsers); setTimeout fallback keeps this testable/SSR-safe. */
function scheduleFrame(cb: () => void): number {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(cb);
  return window.setTimeout(cb, 120) as unknown as number;
}
function cancelFrame(id: number): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
  else window.clearTimeout(id);
}

export function QrScanner({ onDecode, onCancel, label }: QrScannerProps) {
  const { t } = useTranslation('common');
  const displayLabel = label ?? t('qrScanner.defaultLabel');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const decodingRef = useRef(false);
  const decodedRef = useRef(false);
  // Ref-mirrored callback — the decode loop reads the LATEST onDecode without
  // needing it in the mount effect's deps (which would restart the camera on
  // every parent re-render that passes a fresh function reference).
  const onDecodeRef = useRef(onDecode);
  useEffect(() => {
    onDecodeRef.current = onDecode;
  }, [onDecode]);

  const [state, setState] = useState<ScannerState>('permission');
  const [cameraError, setCameraError] = useState<CameraError | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);

  const stopStream = useCallback(() => {
    for (const trackItem of streamRef.current?.getTracks() ?? []) {
      trackItem.stop();
    }
    streamRef.current = null;
  }, []);

  const stopDecodeLoop = useCallback(() => {
    if (frameRef.current !== null) {
      cancelFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });

        if (cancelled) {
          for (const trackItem of stream.getTracks()) trackItem.stop();
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const [videoTrack] = stream.getVideoTracks();
        const capabilities = videoTrack?.getCapabilities?.();
        setTorchAvailable(!!(capabilities as { torch?: boolean } | undefined)?.torch);

        const detector = await loadDetector();
        if (cancelled) return;

        setState('scanning');
        decodingRef.current = false;

        const tick = () => {
          if (cancelled || decodedRef.current) return;
          const video = videoRef.current;
          if (video && video.readyState >= 2 && !decodingRef.current) {
            decodingRef.current = true;
            detector
              .detect(video)
              .then((results) => {
                decodingRef.current = false;
                const match = results[0]?.rawValue;
                if (match && !decodedRef.current && !cancelled) {
                  decodedRef.current = true;
                  setState('decoded');
                  stopDecodeLoop();
                  stopStream();
                  onDecodeRef.current(match);
                }
              })
              .catch(() => {
                // Transient decode errors (e.g. a mid-frame resize) are not
                // fatal — just skip this frame and keep scanning.
                decodingRef.current = false;
              });
          }
          if (!decodedRef.current && !cancelled) {
            frameRef.current = scheduleFrame(tick);
          }
        };
        frameRef.current = scheduleFrame(tick);
      } catch (err: unknown) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : 'unknown';
        if (name === 'NotAllowedError') setCameraError('NotAllowedError');
        else if (name === 'NotFoundError') setCameraError('NotFoundError');
        else if (name === 'OverconstrainedError') setCameraError('OverconstrainedError');
        else setCameraError('unknown');
        setState('error');
      }
    }

    void start();

    return () => {
      cancelled = true;
      stopDecodeLoop();
      stopStream();
    };
  }, [stopDecodeLoop, stopStream]);

  async function toggleTorch() {
    const [videoTrack] = streamRef.current?.getVideoTracks() ?? [];
    if (!videoTrack) return;
    const next = !torchOn;
    try {
      await videoTrack.applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] });
      setTorchOn(next);
    } catch {
      // Torch toggle failing is non-fatal — scanning continues without it.
    }
  }

  // ── Error states (mirrors CameraCapture) ────────────────────────────────

  if (state === 'error' && cameraError === 'NotAllowedError') {
    return (
      <div className="flex flex-col gap-3 rounded-sm border border-destructive/60 bg-destructive/10 p-4">
        <p className="jq-body-sm font-semibold text-destructive">
          {t('qrScanner.permissionDeniedTitle')}
        </p>
        <p className="jq-body-sm text-muted-foreground">{t('qrScanner.permissionDeniedBody')}</p>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full text-[14px]"
          onClick={onCancel}
        >
          <RotateCcw className="size-4" />
          {t('qrScanner.close')}
        </Button>
      </div>
    );
  }

  if (
    state === 'error' &&
    (cameraError === 'NotFoundError' || cameraError === 'OverconstrainedError')
  ) {
    return (
      <div className="flex flex-col gap-3 rounded-sm border border-border bg-card p-4">
        <p className="jq-body-sm text-muted-foreground">{t('qrScanner.noCamera')}</p>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full text-[14px]"
          onClick={onCancel}
        >
          <RotateCcw className="size-4" />
          {t('qrScanner.close')}
        </Button>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex flex-col gap-3 rounded-sm border border-destructive/60 bg-destructive/10 p-4">
        <p className="jq-body-sm text-destructive">{t('qrScanner.unknownError')}</p>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full text-[14px]"
          onClick={onCancel}
        >
          {t('qrScanner.close')}
        </Button>
      </div>
    );
  }

  // ── Live viewfinder ────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-3">
      <div className="relative overflow-hidden rounded-sm bg-black aspect-video">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          title={t('qrScanner.viewfinderTitle', { label: displayLabel })}
          className="w-full h-full object-cover"
        />
        {state !== 'scanning' && state !== 'decoded' && (
          <div
            role="status"
            className="absolute inset-0 flex items-center justify-center bg-black/60"
          >
            <span className="jq-caption text-white">{t('qrScanner.loading')}</span>
          </div>
        )}
        {state === 'scanning' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <ScanLine className="size-16 text-white/70" strokeWidth={1} aria-hidden />
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="ghost"
          className="h-12 flex-1 text-[14px]"
          onClick={onCancel}
          aria-label={t('qrScanner.cancelAria')}
        >
          <X className="size-4" />
          {t('qrScanner.cancel')}
        </Button>
        {torchAvailable && (
          <Button
            type="button"
            variant="ghost"
            className="h-12 flex-1 text-[14px]"
            onClick={() => void toggleTorch()}
            aria-label={t('qrScanner.torchAria')}
          >
            {torchOn ? <FlashlightOff className="size-4" /> : <Flashlight className="size-4" />}
            {torchOn ? t('qrScanner.torchOff') : t('qrScanner.torchOn')}
          </Button>
        )}
      </div>
    </div>
  );
}
